/**
 * Sales Scripts — shared constants for the script container + item node
 * kinds. A Sales Script is a `type: "flow"` container with
 * `data.kind === "script-container"` (all container mechanics inherited);
 * it is NEVER automation-bound, so the map⟷editor bridge ignores it and
 * everything inside it. Script items are their own React Flow node type
 * ("scriptItem") so the card can show the full text a rep reads aloud.
 */

export const SCRIPT_CONTAINER_KIND = "script-container";

export type ScriptItemKind =
  | "script-question"
  | "script-statement"
  | "script-transition"
  | "script-action"
  | "script-context";

export const SCRIPT_ITEM_META: Record<
  ScriptItemKind,
  { tag: string; color: string; bg: string; hint: string }
> = {
  "script-question": {
    tag: "Question",
    color: "#2563eb",
    bg: "#dbeafe",
    hint: "Something the rep ASKS — discovery, qualification, closing questions",
  },
  "script-statement": {
    tag: "Statement",
    color: "#0f766e",
    bg: "#ccfbf1",
    hint: "Something the rep SAYS — pitch lines, value props, answers",
  },
  "script-transition": {
    tag: "Transition",
    color: "#9333ea",
    bg: "#f3e8ff",
    hint: "A bridge between script sections — how to move the call forward",
  },
  "script-action": {
    tag: "Action",
    color: "#ea580c",
    bg: "#ffedd5",
    hint: "Something the rep DOES — share a screen, show Ads Manager, send a link",
  },
  "script-context": {
    tag: "Context",
    color: "#64748b",
    bg: "#e2e8f0",
    hint: "Notes for the rep — background, objection handling, reminders. Not spoken.",
  },
};

export function isScriptItemKind(kind: string): kind is ScriptItemKind {
  return kind in SCRIPT_ITEM_META;
}

export function isScriptContainer(d: { kind?: string } | undefined): boolean {
  return d?.kind === SCRIPT_CONTAINER_KIND;
}

/** One recorded click during a live run (mirrors the API's path entries). */
export type ScriptPathEntry = {
  nodeId: string;
  label: string;
  type: string;
  at: string;
  /** The branch taken to get here: the label of the edge from the previous
   *  visited card ("Yes", "No", "Too expensive"…). Absent = unlabeled hop. */
  via?: string;
};

/** A Sales Script run as the API returns it (camelCase, path parsed). */
export type ScriptRunView = {
  id: string;
  mapId: string;
  scriptNodeId: string;
  scriptName: string;
  userId: string;
  userName: string;
  contactId: string | null;
  contactName: string;
  status: string; // active | ended | discarded
  path: ScriptPathEntry[];
  pathCount: number;
  /** Rep's free-form call notes (editable live and post-call). */
  notes: string;
  startedAt: string;
  endedAt: string | null;
};
